/* eslint-disable @typescript-eslint/no-explicit-any */

import type { AddToCartRequest, CartListItem, CartTotalsResponse } from '@/types/cartAccs'
import { wrapGqlRequest } from '@/services/graphqlEndPointCommonSetting/wrapGqlRequest'

type ConvertCartSuccess = {
    success: true
    merged: boolean
    fallback?: true
    customerCartId?: string
}

export const useCustomerCartAccsService = () => {
    const guestCartStore = useGuestCartAccsStore()
    const { clearGuestCookie } = guestCartStore
    const { quoteId: guestQuoteId } = storeToRefs(guestCartStore)

    const createCustomerCart = async (): Promise<{
        data: Ref<string | undefined>
        error: Ref<Error | null>
        pending: Ref<boolean>
    }> => {
        // Reactive state for API response
        const data = ref<string | undefined>(undefined)
        const error = ref<Error | null>(null)
        const pending = ref<boolean>(true)

        // GraphQL mutation to create customer cart
        const query = `
        {
            customerCart {
                id
            }
        }    
    `

        try {
            // Execute GraphQL mutation using shared wrapper
            // "createCustomerCart" extracts that node from GraphQL response
            const response = await wrapGqlRequest(query, {}, 'customerCart')

            // Store cart ID in same format as REST
            data.value = response?.id
        } catch (err: any) {
            // Capture GraphQL or network errors
            error.value = err
        } finally {
            // Stop loading state
            pending.value = false
        }

        // Return same structure as useApiFetch (no breaking change)
        return { data, error, pending }
    }

    const fetchCartTotal = async (): Promise<{
        data: Ref<CartTotalsResponse | undefined>
        error: Ref<Error | null>
        pending: Ref<boolean>
    }> => {
        const data = ref<CartTotalsResponse | undefined>(undefined)
        const error = ref<Error | null>(null)
        const pending = ref<boolean>(true)

        const query = `
            query GetCustomerCartTotals {
            customerCart {
                id
                total_quantity
            }
            }
        `

        try {
            const result = await wrapGqlRequest(query, {}, 'customerCart')

            // Keep same CartTotalsResponse shape
            data.value = result as CartTotalsResponse
        } catch (err: any) {
            error.value = err
        } finally {
            pending.value = false
        }

        return { data, error, pending }
    }

    const addToCustomerCart = async (
        payload: AddToCartRequest
    ): Promise<{
        data: Ref<CartListItem | undefined>
        error: Ref<Error | null>
        pending: Ref<boolean>
    }> => {
        const data = ref<CartListItem | undefined>(undefined)

        // Stores any API or GraphQL error
        const error = ref<Error | null>(null)

        // Controls loading state during API call
        const pending = ref<boolean>(true)

        // Remove order_total because GraphQL does not need it in cartItems payload
        const { order_total, ...payloadToSend } = payload

        // Get current cart ID
        const createCustomerCartResponse = await createCustomerCart()

        // GraphQL variables formatted for addProductsToCart mutation
        const variables = {
            cartId: createCustomerCartResponse.data.value,
            cartItems: [
                {
                    sku: payloadToSend.sku, // Product SKU
                    quantity: Number(payloadToSend.quantity) || 0, // Quantity as number
                    entered_options: payloadToSend.entered_options ?? [], // Custom options (designer project,builder id  etc.)
                },
            ],
        }

        // GraphQL mutation to add products to  login user
        const mutation = `
                mutation AddProductsToCart($cartId: String!, $cartItems: [CartItemInput!]!) {
                    addProductsToCart(cartId: $cartId, cartItems: $cartItems) {
                        cart {
                            id
                            total_quantity
                        }
                        user_errors {
                            code
                            message
                        }
                    }
                }
`

        try {
            // Execute GraphQL mutation using shared wrapper
            const result = await wrapGqlRequest(mutation, variables, 'addProductsToCart')

            // Handle business-level GraphQL errors returned from Magento
            if (result?.user_errors?.length) {
                throw new Error(result.user_errors.map((e: any) => e.message).join(', '))
            }

            // Store GraphQL result in CartListItem format (keeps old flow intact)
            data.value = result as CartListItem
        } catch (err: any) {
            // Save error so UI / store can react
            error.value = err
        } finally {
            // Stop loading state once request finishes
            pending.value = false
        }

        // Return same structure as useApiFetch (no breaking change)
        return { data, error, pending }
    }

    const ensureCustomerCartExists = async (): Promise<void> => {
        // Try to fetch cart totals to verify cart exists
        const { data, error } = await fetchCartTotal()
        if (error?.value) {
            if ((error.value as any).statusCode === 404) {
                const { error: createError } = await createCustomerCart()
                if (createError?.value) {
                    throw createError.value
                }
            } else {
                throw error.value
            }
        }

        if (!data?.value) {
            const { error: createError } = await createCustomerCart()
            if (createError?.value) {
                throw createError.value
            }
        }
    }

    const convertGuestToCustomerCart = async (): Promise<ConvertCartSuccess> => {
        if (!guestQuoteId?.value) {
            return {
                success: true,
                merged: false,
            }
        }

        // 2) Create customer cart (only once)
        const { data, error: createError } = await createCustomerCart()
        if (createError?.value) {
            throw createError.value // store will catch
        }

        const customerCartId = data?.value

        if (!customerCartId) {
            throw new Error('create customer cart failed')
        }

        const mutation = `mutation MergeCarts($source_cart_id: String!, $destination_cart_id: String!) {
        mergeCarts(
            source_cart_id: $source_cart_id
            destination_cart_id: $destination_cart_id
        ) {
            email
            gift_receipt_included
            id
            is_virtual
            printed_card_included
            total_quantity
        }
    }
    
    `
        const variables = {
            source_cart_id: guestQuoteId.value,
            destination_cart_id: customerCartId,
        }

        try {
            // Execute GraphQL mutation using shared wrapper
            // "createCustomerCart" extracts that node from GraphQL response
            await wrapGqlRequest(mutation, variables, 'mergeCarts')

            // merge success => clear guest cookie
            clearGuestCookie()

            return {
                success: true,
                merged: true,
                customerCartId,
            }
        } catch (error: any) {
            // Both conversion and fallback failed - let error bubble up
            // Error will be reported to Datadog in the store layer
            // eslint-disable-next-line no-console
            console.warn('Guest to customer cart conversion failed:', error)
            throw error
        }
    }

    // const removeDesignProjectFromCartApi = async (itemId) => {
    //     const url = `${magentoApiUrl}/rest/V1/carts/mine/items/${itemId}`
    //     const { data, error, pending, refresh } = await useApiFetch(url, {
    //         method: 'DELETE',
    //         headers: { ...authHeader() },
    //     })
    //     return { data, error, pending, refresh }
    // }

    return {
        createCustomerCart,
        fetchCartTotal,
        addToCustomerCart,
        convertGuestToCustomerCart,
        ensureCustomerCartExists,
    }
}
